import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { FeatureProvider } from './contexts/FeatureContext';
import FeatureGate from './components/FeatureGate';
import AuthCallback from './components/AuthCallback';
import CookieConsent from './components/CookieConsent';
import LiveChat from './components/LiveChat';
import AppLayout from './components/AppLayout';
import ChallengeInviteModal from './components/ChallengeInviteModal';
import ChallengeAcceptedHandler from './components/ChallengeAcceptedHandler';
import { BlishAPI } from '@/apis';
import { useStore } from './store/useStore';
import '@/App.css';

// Core pages
import Landing from './pages/LandingBrandKit';

// Lazy-loaded pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const KYCBasic = lazy(() => import('./pages/KYCBasic'));
const KYCWithdrawal = lazy(() => import('./pages/KYCWithdrawal'));
const CreateChallenge = lazy(() => import('./pages/CreateChallenge'));
const ChallengeDetail = lazy(() => import('./pages/ChallengeDetail'));
const Challenges = lazy(() => import('./pages/Challenges'));
const ChallengeHistory = lazy(() => import('./pages/ChallengeHistory'));
const Wallet = lazy(() => import('./pages/Wallet'));
const Profile = lazy(() => import('./pages/Profile'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Streams = lazy(() => import('./pages/Streams'));
const Tournaments = lazy(() => import('./pages/Tournaments'));
const TournamentDetail = lazy(() => import('./pages/TournamentDetail'));
const LiveStream = lazy(() => import('./pages/LiveStream'));
const Matchmaking = lazy(() => import('./pages/Matchmaking'));
const Players = lazy(() => import('./pages/Players'));
const MyBackings = lazy(() => import('./pages/MyBackings'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Messages = lazy(() => import('./pages/Messages'));
const Play = lazy(() => import('./pages/Play'));
const PlayGame = lazy(() => import('./pages/PlayGame'));
const ChessGame = lazy(() => import('./pages/ChessGame'));
const Games = lazy(() => import('./pages/Games'));
const AlignmentGame = lazy(() => import('./pages/AlignmentGame'));
const OthelloGame = lazy(() => import('./pages/OthelloGame'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Features = lazy(() => import('./pages/Features'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Contact = lazy(() => import('./pages/Contact'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const RiskDisclosure = lazy(() => import('./pages/RiskDisclosure'));

const PageLoader = () => (
    <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-lime border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-lime font-mono">Loading...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const accessToken = useStore((state) => state.accessToken);
  if (!accessToken) return <Navigate to="/" replace />;
  return children;
};

const AppRouter = () => {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/features" element={<Features />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/risk-disclosure" element={<RiskDisclosure />} />

        {/* Public stream routes — anyone can view */}
        <Route path="/streams" element={<Streams />} />
        <Route path="/livestream" element={<LiveStream />} />

        {/* Redirect old /login and /register to homepage */}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/register" element={<Navigate to="/" replace />} />
        <Route path="/auth" element={<Navigate to="/" replace />} />

        {/* Protected routes inside AppLayout (sidebar) */}
        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/kyc" element={<FeatureGate><KYCBasic /></FeatureGate>} />
          <Route path="/kyc/basic" element={<FeatureGate><KYCBasic /></FeatureGate>} />
          <Route path="/kyc/verify" element={<Navigate to="/kyc/basic" replace />} />
          <Route path="/kyc/withdrawal" element={<FeatureGate><KYCWithdrawal /></FeatureGate>} />
          <Route path="/create-challenge" element={<FeatureGate><CreateChallenge /></FeatureGate>} />
          <Route path="/challenge/:id" element={<ChallengeDetail />} />
          <Route path="/challenges" element={<FeatureGate><Challenges /></FeatureGate>} />
          <Route path="/challenge-history" element={<FeatureGate><ChallengeHistory /></FeatureGate>} />
          <Route path="/wallet" element={<FeatureGate><Wallet /></FeatureGate>} />
          <Route path="/profile/:id" element={<FeatureGate><Profile /></FeatureGate>} />
          <Route path="/leaderboard" element={<FeatureGate><Leaderboard /></FeatureGate>} />
          <Route path="/tournaments" element={<FeatureGate><Tournaments /></FeatureGate>} />
          <Route path="/tournament/:id" element={<TournamentDetail />} />
          <Route path="/matchmaking" element={<FeatureGate><Matchmaking /></FeatureGate>} />
          <Route path="/players" element={<FeatureGate><Players /></FeatureGate>} />
          <Route path="/me/backings" element={<FeatureGate><MyBackings /></FeatureGate>} />
          <Route path="/notifications" element={<FeatureGate><Notifications /></FeatureGate>} />
          <Route path="/messages" element={<FeatureGate><Messages /></FeatureGate>} />
          <Route path="/play" element={<FeatureGate><Play /></FeatureGate>} />
          <Route path="/play/:gameId" element={<FeatureGate><PlayGame /></FeatureGate>} />
          <Route path="/play/chess/:matchId" element={<FeatureGate><ChessGame /></FeatureGate>} />
          <Route path="/play/alignment/:matchId" element={<FeatureGate><AlignmentGame /></FeatureGate>} />
          <Route path="/play/othello/:matchId" element={<FeatureGate><OthelloGame /></FeatureGate>} />
          <Route path="/games" element={<FeatureGate><Games /></FeatureGate>} />
        </Route>

      </Routes>
    </Suspense>
  );
};

function App() {
  const { accessToken, setAuth, user: storeUser } = useStore();

  useEffect(() => {
    const initAuth = async () => {
      if (accessToken && !storeUser) {
        try {
          const { data } = await BlishAPI.auth.getProfile();
          setAuth(data, accessToken, storeUser?.refreshToken || data.refreshToken);
        } catch (error) {
          useStore.getState().logout();
        }
      }
    };

    initAuth();
  }, []);

  return (
    <FeatureProvider>
      <BrowserRouter>
        <AppRouter />
        <CookieConsent />
        {storeUser && <LiveChat />}
        {storeUser && <ChallengeInviteModal />}
        {storeUser && <ChallengeAcceptedHandler />}
      </BrowserRouter>
      <Toaster position="top-right" />
    </FeatureProvider>
  );
}

export default App;
